import {fromUTM} from './gaia/getSyncMapboxStyle/getGridMapboxStyle/UTM';
import {fromMGRS} from './gaia/getSyncMapboxStyle/getGridMapboxStyle/MGRS';

function validateCoordinates(coords: number[] | undefined) {
    if (!coords) return;
    if (coords[0] >= -90 && coords[0] <= 90 && coords[1] >= -180 && coords[1] <= 180) return coords;
    return;
}

export default function parseCoordinates(text: string) {
    text = text
        // Remove leading & trailing whitespace
        .trim()
        // Remove smart single quotes and apostrophes from windows
        .replace(/[\u2018\u2019\u201A]/g, "'")
        // Remove double quotes
        .replace(/[\u201C\u201D\u201E]/g, '"')
        // Remove en-dash & em-dash
        .replace(/[\u2013\u2014]/g, '-');

    const ddMatch = validateCoordinates(decimalDegrees(text));
    if (ddMatch) return ddMatch;

    const directionalDDMatch = validateCoordinates(directionalDecimalDegrees(text));
    if (directionalDDMatch) return directionalDDMatch;

    const dmsMatch = validateCoordinates(degreesMinutesSeconds(text));
    if (dmsMatch) return dmsMatch;

    const utmMatch = validateCoordinates(utm(text));
    if (utmMatch) return utmMatch;

    const mgrsMatch = validateCoordinates(mgrs(text));
    if (mgrsMatch) return mgrsMatch;

    return;
}

const ddRegex = new RegExp(
    /^([+-]?\d+\.?\d+) *[d°]? *([NnSs]?)[, /|]*([+-]?\d+\.?\d+) *[d°]? *([EwWw]?)$/
);

const directionalDecimalRegex = new RegExp(
    /^([NnSs])[ ?]*(\d+\.?\d+) *[d°]? *[, /|]*([EwWw])[ ?]*(\d+\.?\d+) *[d°]? *$/
);

const dmsRegex = new RegExp(
    /^([NnSs]?)([+-]?\d{0,3}) *[d:°ºdD]? *(\d{0,3}\.?\d{0,6}) *[:'′mM]? *(\d{0,3}\.?\d{0,6})? *[:"″sS]? *([NnSs]?)[, /|]*([EeWw]?)([+-]?\d{0,3}\.?\d{0,6}) *[d:°ºdD]? *(\d{0,3}\.?\d{0,6}) *[:'’′mM]? *(\d{0,3}\.?\d{0,6})? *[:"”″sS]? *([EwWw]?)$/
);

const utmRegex = new RegExp(
    /^(\d{1,2})\s*([NPQRSTUVWX]?)([CDEFGHJKLM]?)\s*0?(\d+[,\\.]?\d+)[Eem\s]*(\d+[,\\.]?\d+)[Nnm\s]*$/
);

const mgrsRegex = new RegExp(/^\d{1,2}[a-zA-Z]{2,3}(\d+)$/);

function decimalDegrees(input: string) {
    const parsed = ddRegex.exec(input);
    if (!parsed || parsed.length !== 5) return;

    let lat = parseFloat(parsed[1]);
    let lng = parseFloat(parsed[3]);

    if (parsed[2].toUpperCase() === 'S' && lat > 0) {
        lat = lat * -1;
    }
    if (parsed[4].toUpperCase() === 'W' && lng > 0) {
        lng = lng * -1;
    }

    return [lat, lng];
}

function directionalDecimalDegrees(input: string) {
    const parsed = directionalDecimalRegex.exec(input);
    if (!parsed || parsed.length !== 5) return;

    let lat = parseFloat(parsed[2]);
    let lng = parseFloat(parsed[4]);

    if (parsed[1].toUpperCase() === 'S') {
        lat = lat * -1;
    }
    if (parsed[3].toUpperCase() === 'W') {
        lng = lng * -1;
    }

    return [lat, lng];
}

function degreesMinutesSeconds(input: string) {
    const parsed = dmsRegex.exec(input);
    if (!parsed || parsed.length !== 11) return;

    let lat = parseFloat(parsed[2]);
    const latDecimals = (parseFloat(parsed[3]) || 0) / 60 + (parseFloat(parsed[4]) || 0) / 3600;
    if (lat > 0) lat += latDecimals;
    else lat -= latDecimals;

    if ((parsed[1] || parsed[5]).toUpperCase() === 'S') lat = lat * -1;

    let lng = parseFloat(parsed[7]);
    const lngDecimals = (parseFloat(parsed[8]) || 0) / 60 + (parseFloat(parsed[9]) || 0) / 3600;
    if (lng > 0) lng += lngDecimals;
    else lng -= lngDecimals;

    if ((parsed[6] || parsed[10]).toUpperCase() === 'W') lng = lng * -1;

    return [lat, lng];
}

function utm(input: string) {
    const parsed = utmRegex.exec(input);
    if (!parsed || parsed.length !== 6) return;

    return fromUTM({
        zoneNumber: parseInt(parsed[1]),
        zoneLetter: parsed[2],
        easting: parseFloat(parsed[4]),
        northing: parseFloat(parsed[5]),
        accuracy: Math.min(parsed[4].length, parsed[5].length),
    }).reverse();
}

function mgrs(input: string) {
    const parsed = mgrsRegex.exec(input.replace(/\s/g, ''));
    if (!parsed || parsed[1].length % 2 !== 0) return;

    const mgrsCoord = fromMGRS(input);
    if (!mgrsCoord) return;

    return [(mgrsCoord[1] + mgrsCoord[3]) / 2, (mgrsCoord[0] + mgrsCoord[2]) / 2];
}
